import { redistributeColSizesAfterInsert } from '../../serializers/tableColSizes';

// Pure path-math for selecting an entire table row or column. Shared by the
// cell context menu (tableElements) and the row/column drag-handles
// (TableHandlesOverlay) so the grid-coordinate logic lives in one place; each
// caller keeps its own side effects (focus, scroll restoration). Returns null
// paths when the table/row is empty so callers can short-circuit before
// touching the editor.
// Stable unique id for a table cell (td/th). @platejs/table keys its merged-cell
// index matrix (computeCellIndices → `_cellIndices`) on `cell.id`; with no id
// every cell collapses to {col:0,row:0}, so merged cells become unselectable and
// the per-cell index cache is defeated (computeCellIndices re-runs O(cells) every
// render). NodeIdPlugin — which would stamp these — is only registered under
// enableBlockDnd (off everywhere), so a dedicated cell normalizer assigns them
// instead. Mirrors the ColumnItemPlugin `col-...` id scheme in usePlateEditorLogic.
export function generateCellId(): string {
    return `cell-${Math.random().toString(36).slice(2, 10)}`;
}

// Split-eligibility for the "Split cell" context-menu entry, computed from the
// cell node itself so no editor-wide selection subscription is needed (the
// library's useTableMergeState registered three per cell — see the disableMerge
// option in usePlateEditorLogic). @platejs/table's splitTableCell operates on
// colSpan/rowSpan directly and is not merge-gated, so it stays functional with
// merging disabled.
export function isCellSplitEligible(cellElement: any): boolean {
    return (cellElement?.colSpan ?? 1) > 1 || (cellElement?.rowSpan ?? 1) > 1;
}

export function calculateSelectionRange(
    axis: 'row' | 'column',
    tableNode: any,
    tablePath: number[],
    index: number,
): { start: number[] | null; end: number[] | null } {
    if (!tableNode?.children?.length) return { start: null, end: null };
    if (axis === 'row') {
        const row = tableNode.children[index];
        if (!row?.children?.length) return { start: null, end: null };
        return { start: [...tablePath, index, 0], end: [...tablePath, index, row.children.length - 1] };
    }
    return { start: [...tablePath, 0, index], end: [...tablePath, tableNode.children.length - 1, index] };
}

// A cell's structural child-index only equals its visual column when the
// table has no merges. A colSpan/rowSpan cell reserves extra grid slots that
// push every later sibling's child-index one or more columns short of its
// visual position, and a rowSpan cell's reservation carries into rows below
// it that never list that cell as a child at all. calculateRectRange needs
// the VISUAL rectangle (what the highlight shows), so when the table has any
// span it resolves through this occupancy grid instead of raw child-index
// math. `spans[row][childIndex]` is a cell's own visual column range;
// `grid[row][col]` is the structural (row, childIndex) that occupies a given
// visual cell, which may belong to an earlier row via rowSpan.
type CellSpan = { start: number; end: number };
type CellRef = { row: number; childIndex: number };

function buildVisualGrid(tableNode: any): { grid: CellRef[][]; spans: CellSpan[][] } {
    const rows: any[] = tableNode?.children ?? [];
    const grid: CellRef[][] = rows.map(() => []);
    const spans: CellSpan[][] = rows.map(() => []);
    for (let row = 0; row < rows.length; row++) {
        const cells: any[] = rows[row]?.children ?? [];
        let col = 0;
        for (let childIndex = 0; childIndex < cells.length; childIndex++) {
            while (grid[row][col]) col++;
            const cell = cells[childIndex];
            const colSpan = Math.max(1, cell?.colSpan ?? 1);
            const rowSpan = Math.max(1, cell?.rowSpan ?? 1);
            const start = col;
            const end = col + colSpan - 1;
            spans[row][childIndex] = { start, end };
            for (let r = row; r < Math.min(row + rowSpan, rows.length); r++) {
                for (let c = start; c <= end; c++) grid[r][c] = { row, childIndex };
            }
            col = end + 1;
        }
    }
    return { grid, spans };
}

function tableHasSpans(tableNode: any): boolean {
    return !!tableNode?.children?.some(
        (row: any) => row?.children?.some((cell: any) => (cell?.colSpan ?? 1) > 1 || (cell?.rowSpan ?? 1) > 1),
    );
}

// Pure path-math for Shift+Click range selection: given two arbitrary cell
// paths (anchor + clicked) in the SAME table, returns the top-left and
// bottom-right corners of the rectangle spanning both — i.e. Excel's
// "shift-click extends to a rectangle between the two cells" behavior.
// Cell paths are [...tablePath, rowIdx, colIdx] (shared assumption with
// moveCellSelection in cellSelection.ts). Returns null corners when the two
// paths don't share a table path prefix (shift-click across two different
// tables — not a valid rectangle, caller should ignore).
//
// `tableNode` is the actual table element (rows -> cells, with colSpan/
// rowSpan) so merged cells resolve through the visual grid above; pass
// null/undefined when the caller has no merges to account for (or none at
// all, e.g. plain-path unit tests) and the raw child-index math applies as-is.
export function calculateRectRange(
    tableNode: any,
    anchorCellPath: number[],
    targetCellPath: number[],
): { start: number[] | null; end: number[] | null } {
    if (anchorCellPath.length !== targetCellPath.length || anchorCellPath.length < 2) return { start: null, end: null };
    const tableLen = anchorCellPath.length - 2;
    const anchorTablePath = anchorCellPath.slice(0, tableLen);
    const targetTablePath = targetCellPath.slice(0, tableLen);
    if (anchorTablePath.join() !== targetTablePath.join()) return { start: null, end: null };

    const anchorRow = anchorCellPath[tableLen];
    const anchorChildIndex = anchorCellPath[tableLen + 1];
    const targetRow = targetCellPath[tableLen];
    const targetChildIndex = targetCellPath[tableLen + 1];

    if (!tableHasSpans(tableNode)) {
        const minRow = Math.min(anchorRow, targetRow);
        const maxRow = Math.max(anchorRow, targetRow);
        const minCol = Math.min(anchorChildIndex, targetChildIndex);
        const maxCol = Math.max(anchorChildIndex, targetChildIndex);
        return { start: [...anchorTablePath, minRow, minCol], end: [...anchorTablePath, maxRow, maxCol] };
    }

    const { grid, spans } = buildVisualGrid(tableNode);
    const anchorSpan = spans[anchorRow]?.[anchorChildIndex];
    const targetSpan = spans[targetRow]?.[targetChildIndex];
    if (!anchorSpan || !targetSpan) return { start: null, end: null };

    const minRow = Math.min(anchorRow, targetRow);
    const maxRow = Math.max(anchorRow, targetRow);
    const minVisualCol = Math.min(anchorSpan.start, targetSpan.start);
    const maxVisualCol = Math.max(anchorSpan.end, targetSpan.end);

    const startRef = grid[minRow]?.[minVisualCol];
    const endRef = grid[maxRow]?.[maxVisualCol];
    if (!startRef || !endRef) return { start: null, end: null };

    return {
        start: [...anchorTablePath, startRef.row, startRef.childIndex],
        end: [...anchorTablePath, endRef.row, endRef.childIndex],
    };
}

// Context-menu "Insert column left/right". insertTableColumn splices a 0 into
// a fully-sized table's colSizes, and a 0 column renders at zero width when the
// existing columns already fill the table (the invisible-inserted-column bug —
// full story on redistributeColSizesAfterInsert). After the library transform
// runs, locate the spliced 0 by diffing colSizes against the pre-insert array
// and replace the whole array with the redistributed widths. Diffing (rather
// than precomputing the index from the clicked cell) stays correct however the
// transform resolves the source cell from the selection. `cellElement` only
// anchors the table lookup; tables without colSizes, or already in the
// auto-width regime (any 0 entry), are left to the transform's own behaviour —
// leftover width exists there and the spliced 0 renders fine.
export function insertTableColumnSized(editor: any, cellElement: any, before?: boolean): void {
    try {
        const cellPath: number[] | undefined = editor?.api?.findPath?.(cellElement);
        const tablePath = cellPath && cellPath.length >= 2 ? cellPath.slice(0, -2) : undefined;
        const tableBefore: any = tablePath && editor?.api?.node?.(tablePath)?.[0];
        const prev: number[] | null =
            Array.isArray(tableBefore?.colSizes) &&
            tableBefore.colSizes.every((w: any) => typeof w === 'number' && Number.isFinite(w) && w > 0)
                ? [...tableBefore.colSizes]
                : null;

        editor?.tf?.insert?.tableColumn?.(before ? { before: true } : undefined);

        if (!prev || !tablePath) return;
        const tableAfter: any = editor?.api?.node?.(tablePath)?.[0];
        const next: unknown = tableAfter?.colSizes;
        if (!Array.isArray(next) || next.length !== prev.length + 1) return;
        const insertIndex = next.findIndex((w) => !w);
        if (insertIndex < 0) return;
        const rest = next.filter((_, i) => i !== insertIndex);
        if (rest.some((w, i) => w !== prev[i])) return;
        editor.tf.setNodes({ colSizes: redistributeColSizesAfterInsert(prev, insertIndex) }, { at: tablePath });
    } catch {
        /* defensive — table may have been mutated under us */
    }
}
